import type { CSSProperties, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { useServerMarkup } from './server-markup'

// Six decorative SVG loops for the Solutions cards; clock, colours and reduced motion are in docs/notes/app/landing.md § 3.

export type SolutionKind = 'evaluate' | 'describe' | 'teams' | 'market' | 'cross' | 'api'

/** A per-element offset into the shared cycle, plus whatever the keyframes read from the element. */
const vars = (lag: number, extra: Record<string, string | number> = {}): CSSProperties => ({
  '--solution-lag': `${lag}s`,
  ...extra,
})

// The test's three statements, top to bottom. The first is the one being ranked, so it is drawn last.
const STATEMENTS = [
  { top: 26, width: 16, className: 'solution-statement-2' },
  { top: 39, width: 22, className: 'solution-statement-3' },
  { top: 13, width: 20, className: 'solution-statement-1' },
] as const

/**
 * Evaluate: the test's ranking. One statement lifts and is dragged down a slot, then another, then back to the top,
 * while the other two make room. The dragged one is drawn last so it passes over them.
 */
function Evaluate() {
  return (
    <>
      {STATEMENTS.map((s) => (
        <g key={s.top} className={cn('solution-statement', s.className)} style={vars(0)}>
          <rect x="12" y={s.top} width="40" height="10" rx="5" />
          <circle className="solution-statement-dot" cx="18" cy={s.top + 5} r="2.5" />
          <line className="solution-track" x1="24" x2={24 + s.width} y1={s.top + 5} y2={s.top + 5} />
        </g>
      ))}
    </>
  )
}

// A 3 × 3 grid of indicator chips, and the ones picked in turn: relevant (up) or non-relevant (down).
const CHIPS = Array.from({ length: 9 }, (_, i) => ({ i, x: 8 + (i % 3) * 17, y: 15 + Math.floor(i / 3) * 12.5 }))
const PICKS = [
  { chip: 0, relevant: true },
  { chip: 5, relevant: false },
  { chip: 7, relevant: true },
  { chip: 2, relevant: true },
  { chip: 6, relevant: false },
] as const

/** Describe: the wizard's chips get picked one after another, relevant ones solid and non-relevant ones soft, then clear. */
function Describe() {
  return (
    <>
      {CHIPS.map((chip) => (
        <rect key={chip.i} className="solution-chip" x={chip.x} y={chip.y} width="14" height="9" rx="3" />
      ))}
      {PICKS.map((pick, order) => {
        const chip = CHIPS[pick.chip]
        if (!chip) return null
        const cx = chip.x + 7
        const cy = chip.y + 4.5
        const tip = pick.relevant ? -1.3 : 1.3
        return (
          <g key={pick.chip} className="solution-tick" style={vars(order * 0.6)}>
            <rect
              className={pick.relevant ? 'solution-chip-relevant' : 'solution-chip-irrelevant'}
              x={chip.x}
              y={chip.y}
              width="14"
              height="9"
              rx="3"
            />
            <polyline
              className={cn('solution-chevron', !pick.relevant && 'solution-chevron-soft')}
              points={`${cx - 2.5},${cy - tip} ${cx},${cy + tip} ${cx + 2.5},${cy - tip}`}
            />
          </g>
        )
      })}
    </>
  )
}

// Four members × five indicators, scored as a team board does: high, medium or low.
const BOARD = [
  [11, 9, 6, 10, 2],
  [8, 10, 9, 3, 7],
  [4, 11, 3, 9, 5],
  [1, 6, 10, 4, 9],
].map((scores, r) => ({
  id: `member-${r}`,
  y: 15 + r * 9,
  // A column's cells lag by the cursor's step (18% of the cycle), so they bump as it lands on them.
  cells: scores.map((v, c) => ({
    id: `member-${r}-${c}`,
    x: 15 + c * 9,
    lag: c * 1.296,
    tone: v >= 9 ? 'high' : v <= 3 ? 'low' : 'medium',
  })),
}))

/** Teams: a tiny team board; a column cursor steps across the indicators and each column's cells bump as it lands. */
function Teams() {
  return (
    <>
      {BOARD.map((row) => (
        <g key={row.id}>
          <circle className="solution-member" cx="9" cy={row.y + 3.5} r="2.6" />
          {row.cells.map((cell) => (
            <rect
              key={cell.id}
              className={`solution-cell solution-cell-${cell.tone}`}
              x={cell.x}
              y={row.y}
              width="7"
              height="7"
              rx="1.5"
              style={vars(cell.lag)}
            />
          ))}
        </g>
      ))}
      <rect className="solution-cursor" x="13" y="13" width="11" height="38" rx="3" style={vars(0)} />
    </>
  )
}

// A dot plot of the market, one column per band; the highlighted profile sits at the top of the fifth column.
const COLUMNS = [1, 2, 4, 5, 4, 2, 1] as const
const HIGHLIGHT = { column: 4, row: 3 }
const DOTS = COLUMNS.flatMap((count, column) =>
  Array.from({ length: count }, (_, row) => ({ column, row, x: 14 + column * 6, y: 50 - row * 6 })),
)

/** Market: the population lands dot by dot, its distribution curve traces over it, and one profile lights up. */
function Market() {
  const highlight = DOTS.find((dot) => dot.column === HIGHLIGHT.column && dot.row === HIGHLIGHT.row)
  return (
    <>
      {DOTS.filter((dot) => dot !== highlight).map((dot, i) => (
        <circle
          key={`${dot.column}-${dot.row}`}
          className="solution-dot"
          cx={dot.x}
          cy={dot.y}
          r="2"
          style={vars(i * 0.06)}
        />
      ))}
      <path
        className="solution-curve"
        d="M7 49 C 19 48, 22 21, 32 21 S 45 48, 57 49"
        pathLength={100}
        strokeDasharray="100 200"
        style={vars(0)}
      />
      {highlight && (
        <>
          <circle className="solution-ping" cx={highlight.x} cy={highlight.y} r="2.6" style={vars(0)} />
          <circle className="solution-pick" cx={highlight.x} cy={highlight.y} r="2.6" style={vars(0)} />
        </>
      )}
    </>
  )
}

/**
 * Your ten-axis profile, drawn solid: a softly rounded curve through its points rather than a polygon, so
 * where it overlaps the market's it reads calm instead of as a star. Written out rather than computed at
 * render, so the server and the browser print the same markup. This is profile a; app.css's
 * `solution-morph` keyframes hold b and c, in the same M + 10 C + Z commands so it interpolates on the axes.
 */
const PROFILE =
  'M32 13.6 C34.4 13.5 36.6 19.8 39.3 22 C42 24.2 47.7 24.6 48 26.8 C48.3 29 42.5 32.3 41.1 35 C39.8 37.6 41.3 41.2 39.8 42.7 C38.2 44.2 34.9 43.6 32 44 C29.1 44.4 23.9 46.8 22.4 45.3 C20.8 43.8 23.2 38.1 22.5 35.1 C21.8 32.1 17.5 29.5 17.9 27.4 C18.4 25.3 22.8 24.9 25.2 22.6 C27.5 20.3 29.6 13.7 32 13.6Z'
/**
 * The market's, around the same centre and dashed a shade fainter. Its values sit close to yours and change
 * gently from axis to axis, so once aligned the two mostly overlap — yours reaching well past it on some
 * axes and falling short on others is what makes the pair read as a comparison, not one shape drawn twice.
 */
const BENCHMARK =
  'M32 17 C34.1 16.9 39.2 18.5 40.8 19.9 C42.5 21.2 44.8 25.6 45.3 27.7 C45.9 29.7 45.9 34.3 45.3 36.3 C44.8 38.4 42.5 42.9 40.8 44.1 C39.2 45.3 34.3 45.9 32 46 C29.7 46.1 24.4 46.1 22.6 44.9 C20.8 43.8 18.2 38.8 17.7 36.6 C17.2 34.5 17.9 29.7 18.7 27.7 C19.4 25.7 22.1 22 23.8 20.7 C25.4 19.3 29.9 17.1 32 17Z'
// The radar's outer ring, and the five lines through the centre that carry its ten axes.
const GRID = '32,12 43.8,15.8 51,25.8 51,38.2 43.8,48.2 32,52 20.2,48.2 13,38.2 13,25.8 20.2,15.8'
const SPOKES = [
  { x1: 32, y1: 12, x2: 32, y2: 52 },
  { x1: 43.8, y1: 15.8, x2: 20.2, y2: 48.2 },
  { x1: 51, y1: 25.8, x2: 13, y2: 38.2 },
  { x1: 51, y1: 38.2, x2: 13, y2: 25.8 },
  { x1: 43.8, y1: 48.2, x2: 20.2, y2: 15.8 },
] as const

/** Cross: your profile's shape moves to one profile's scores after another, against the market's fixed outline. */
function Cross() {
  return (
    <>
      <polygon className="solution-track" points={GRID} />
      {SPOKES.map((spoke) => (
        <line key={`${spoke.x1}-${spoke.y1}`} className="solution-track" {...spoke} />
      ))}
      <path className="solution-profile solution-profile-benchmark" d={BENCHMARK} />
      <path className="solution-profile solution-profile-morphing" d={PROFILE} style={vars(0)} />
      <circle className="solution-join" cx="32" cy="32" r="2.4" />
    </>
  )
}

// The report's three dimension bars: how far each reaches, and how strongly it is tinted.
const BARS = [
  { y: 36, width: 17, strength: 1 },
  { y: 40.5, width: 12, strength: 0.7 },
  { y: 45, width: 15, strength: 0.45 },
] as const

/**
 * API: the SDK on someone else's page. A browser window holds the page's own content and an empty, dashed slot; the
 * report mounts into the slot and its dimension bars grow in, then it leaves before the next load.
 */
function Api() {
  return (
    <>
      <rect className="solution-window" x="7" y="12" width="50" height="41" rx="5" />
      <line className="solution-track" x1="7" x2="57" y1="21" y2="21" />
      {[11.5, 15.5, 19.5].map((cx) => (
        <circle key={cx} className="solution-light" cx={cx} cy="16.5" r="1.3" />
      ))}
      <rect className="solution-host" x="24" y="15.25" width="28" height="2.5" rx="1.25" />
      {[
        { y: 25, width: 9 },
        { y: 29.5, width: 7 },
        { y: 34, width: 8 },
      ].map((line) => (
        <rect key={line.y} className="solution-host" x="11" y={line.y} width={line.width} height="2.5" rx="1.25" />
      ))}
      <rect className="solution-slot" x="23" y="25" width="30" height="24" rx="2.5" />
      <g className="solution-report" style={vars(0)}>
        <rect className="solution-report-card" x="23" y="25" width="30" height="24" rx="2.5" />
        <circle className="solution-report-avatar" cx="27.5" cy="30" r="2" />
        <rect className="solution-host" x="31" y="29.1" width="12" height="1.8" rx="0.9" />
        {BARS.map((bar, i) => (
          <g key={bar.y}>
            <rect className="solution-report-track" x="27" y={bar.y} width="22" height="2.5" rx="1.25" />
            <rect
              className="solution-report-bar"
              x="27"
              y={bar.y}
              width={bar.width}
              height="2.5"
              rx="1.25"
              fillOpacity={bar.strength}
              style={vars(i * 0.45)}
            />
          </g>
        ))}
      </g>
    </>
  )
}

const ART: Record<SolutionKind, () => ReactNode> = {
  evaluate: Evaluate,
  describe: Describe,
  teams: Teams,
  market: Market,
  cross: Cross,
  api: Api,
}

/** One solution's loop, in a tile tinted with its section's colour, in place of an icon. */
export function SolutionAnimation(props: { kind: SolutionKind; className?: string }) {
  const Art = ART[props.kind]
  // Static art: hydration keeps the server's nodes (landing.md, "Load cost").
  const art = useServerMarkup(<Art />)
  return (
    <span
      aria-hidden
      className={cn(
        'solution-tile grid size-16 place-items-center rounded-xl',
        `solution-${props.kind}`,
        props.className,
      )}
    >
      <svg
        viewBox="0 0 64 64"
        className="solution-art size-16"
        fill="none"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...art}
      />
    </span>
  )
}
